import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Button, Chip, CircularProgress,
  Divider, IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export default function OfferAterkoppling() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [commentsByOffer, setCommentsByOffer] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [offSnap, compSnap] = await Promise.all([
        getDocs(query(collection(db, 'offers'), orderBy('updatedAt', 'desc'))),
        getDocs(collection(db, 'crmCompanies')),
      ]);
      const allOffers = offSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const responding = allOffers.filter(o => o.clientResponse?.status);
      setOffers(responding);
      setCompanies(compSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load comments subcollection for each responding offer
      const commentResults = await Promise.all(
        responding.map(o =>
          getDocs(query(collection(db, 'offers', o.id, 'comments'), orderBy('createdAt', 'asc')))
            .then(snap => [o.id, snap.docs.map(d => ({ id: d.id, ...d.data() }))])
        )
      );
      setCommentsByOffer(Object.fromEntries(commentResults));
      setLoading(false);
    })();
  }, []);

  const approved = offers.filter(o => o.clientResponse?.status === 'approved');
  const commented = offers.filter(o => o.clientResponse?.status === 'commented');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate('/offers')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Återkoppling</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              Svar och kommentarer från kunder
            </Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : offers.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <ChatBubbleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              Inga svar eller kommentarer från kunder ännu.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Kommentarer */}
            {commented.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <ChatBubbleIcon sx={{ fontSize: 18, color: '#1565C0' }} />
                  <Typography fontWeight={700} sx={{ color: '#1565C0' }}>
                    Kommentarer ({commented.length})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {commented.map(offer => (
                    <ResponseCard key={offer.id} offer={offer} companies={companies}
                      comments={commentsByOffer[offer.id] || []} navigate={navigate} />
                  ))}
                </Box>
              </Box>
            )}

            {/* Godkända */}
            {approved.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 18, color: '#2E7D32' }} />
                  <Typography fontWeight={700} sx={{ color: '#2E7D32' }}>
                    Godkända ({approved.length})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {approved.map(offer => (
                    <ResponseCard key={offer.id} offer={offer} companies={companies}
                      comments={commentsByOffer[offer.id] || []} navigate={navigate} />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
}

function ResponseCard({ offer, companies, comments, navigate }) {
  const company = companies.find(c => c.id === offer.companyId);
  const isApproved = offer.clientResponse?.status === 'approved';
  const respondedAt = offer.clientResponse?.respondedAt?.toDate?.();

  return (
    <Paper sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start',
      borderLeft: `4px solid ${isApproved ? '#2E7D32' : '#1565C0'}` }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <Typography fontWeight={700} noWrap>{offer.title}</Typography>
          {offer.offerNumber && (
            <Chip label={offer.offerNumber} size="small"
              sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#f0f0f0' }} />
          )}
          {company && (
            <Typography variant="caption" color="text.secondary">{company.name}</Typography>
          )}
        </Box>

        {/* Comment thread */}
        {comments.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, mb: 1 }}>
            {comments.map(c => {
              const isClient = c.side === 'client';
              return (
                <Box key={c.id} sx={{ display: 'flex', justifyContent: isClient ? 'flex-start' : 'flex-end' }}>
                  <Box sx={{
                    maxWidth: '85%',
                    bgcolor: isClient ? '#EFF6FF' : '#FFF7ED',
                    border: `1px solid ${isClient ? '#BFDBFE' : '#FED7AA'}`,
                    borderRadius: isClient ? '3px 8px 8px 8px' : '8px 3px 8px 8px',
                    p: '5px 10px',
                  }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700,
                        color: isClient ? '#1e40af' : '#c2410c' }}>
                        {c.authorName || (isClient ? 'Kund' : 'Tikab')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.58rem', color: '#bbb' }}>
                        {c.createdAt?.toDate?.()?.toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) || ''}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.8rem', color: '#1a1a1a', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
                      {c.text}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            icon={isApproved ? <CheckCircleIcon /> : <ChatBubbleIcon />}
            label={isApproved ? 'Godkänd' : 'Kommenterad'}
            size="small"
            sx={{
              fontSize: '0.65rem', height: 20,
              bgcolor: isApproved ? '#E8F5E9' : '#E3F2FD',
              color: isApproved ? '#2E7D32' : '#1565C0',
              fontWeight: 600,
              '& .MuiChip-icon': { fontSize: 12 },
            }}
          />
          {respondedAt && (
            <Typography variant="caption" color="text.disabled">
              {respondedAt.toLocaleDateString('sv-SE')} {respondedAt.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
          {offer.clientContact && (
            <Typography variant="caption" color="text.secondary">{offer.clientContact}</Typography>
          )}
        </Box>
      </Box>

      <Button size="small" endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
        onClick={() => navigate(`/offers/${offer.id}`)}
        sx={{ flexShrink: 0, fontSize: '0.72rem' }}>
        Öppna
      </Button>
    </Paper>
  );
}
